import CloudFunctionError, { CloudFunctionErrorType } from './CloudFunctionError.js'
import { httpsCallable } from 'firebase/functions'
import { State } from '../hooks/useAppState.js'
import { functions, auth } from './Firebase.js'
import { CloudFunction } from '../../../pure-js/enums/CloudFunction.js'
import { signOut } from 'firebase/auth'
import { captureException, withScope } from './SentryHelper'
import { sendErrorToSlack } from './SlackHelper'

const formatBackendDetails = (details?: unknown): string => {
  if (!details) {
    return ''
  }

  try {
    return `\nBackend response: ${JSON.stringify(details)}`
  } catch {
    return '\nBackend response: [unserializable payload]'
  }
}

export const callFunction = <TRequest = unknown, TResponse = unknown>(
  cloudfunction: CloudFunction,
  state: State,
  req?: TRequest
): Promise<TResponse> =>
  httpsCallable<TRequest, TResponse>(
    functions,
    cloudfunction
  )(req)
    .then(({ data }) => {
      return data
    })
    .catch(onErrorFactory(cloudfunction, state))

const onErrorFactory = (functionName: CloudFunction, state: State) => (err: CloudFunctionErrorType) => {
  err.message = `Error in ${functionName}: ${err.message}`

  // Handle validation errors (422) - don't auto-logout, just show the error
  if (
    err.code === 'validation-error' ||
    err.message?.includes('Validation failed') ||
    err.message?.includes('There are unassigned participants') ||
    err.message?.includes('has less than 3 members')
  ) {
    // Don't auto-logout for validation errors, just return the error
    return Promise.reject(new CloudFunctionError(err, functionName, state))
  }

  // Global auth error handling - auto-logout on authentication failures
  if (err.code === 'unauthenticated' || err.code === 'permission-denied' || err.message?.includes('not-authorized')) {
    // Send critical auth errors to Slack
    const authError = new Error(`Auth error in ${functionName}: ${err.code} - ${err.message}`)
    sendErrorToSlack(
      authError,
      state.user ? { email: state.user.email, id: state.user.id } : undefined,
      `Cloud Function: ${functionName}`
    ).catch(() => {
      // Ignore Slack errors
    })

    // Send to Sentry (dual reporting)
    withScope((scope) => {
      scope.setTag('cloudFunction', functionName)
      scope.setContext('apiError', { code: err.code, message: err.message })
      if (state.user) scope.setUser({ id: state.user.id, email: state.user.email })
      captureException(authError)
    })

    // Sign out from Firebase
    signOut(auth)
      .then(() => {
        // Redirect to login page
        window.location.href = '/login'
      })
      .catch(() => {
        // Force redirect anyway
        window.location.href = '/login'
      })
  } else {
    // For all other non-validation errors, send to Slack
    const criticalError = new Error(`${functionName} failed: ${err.code || 'unknown'} - ${err.message}`)
    const detailsSuffix = formatBackendDetails(err.details)

    sendErrorToSlack(
      criticalError,
      state.user ? { email: state.user.email, id: state.user.id } : undefined,
      `Cloud Function: ${functionName}${detailsSuffix}`
    ).catch(() => {
      // Ignore Slack errors
    })

    // Send to Sentry (dual reporting)
    withScope((scope) => {
      scope.setTag('cloudFunction', functionName)
      scope.setContext('apiError', {
        code: err.code,
        message: err.message,
        details: err.details
      })
      if (state.user) scope.setUser({ id: state.user.id, email: state.user.email })
      captureException(criticalError)
    })
  }

  return Promise.reject(new CloudFunctionError(err, functionName, state))
}
